import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

/**
 * PulseCTA — primary "Book a working session" style CTA.
 * Subtle breathing halo + micro-nudge on the arrow. Kept restrained; the
 * animation is confined to a soft glow ring, so the button itself remains a
 * clean pill and does not compete with page content.
 *
 * variant: 'green' (default, on cream), 'dark' (for green/ink surfaces),
 *          'ghost' (link-style, no fill, subtle underline pulse)
 */
export default function PulseCTA({
  to = '/contact',
  children = 'Book a working session',
  variant = 'green',
  size = 'md',
  testId = 'pulse-cta',
  eventName = 'Book Session',
  className = '',
}) {
  const sizes = {
    sm: 'px-5 py-2.5 text-[13px]',
    md: 'px-7 py-3.5 text-[14px]',
    lg: 'px-8 py-5 text-[15px]',
  };
  const iconSize = size === 'lg' ? 18 : 16;

  const variants = {
    green: {
      bg: '#22A94F',
      bgHover: '#178838',
      text: '#0F3D22',
      halo: 'rgba(34,169,79,0.45)',
    },
    dark: {
      bg: '#22A94F',
      bgHover: '#7ED4A0',
      text: '#0F3D22',
      halo: 'rgba(126,212,160,0.55)',
    },
  };

  if (variant === 'ghost') {
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`plausible-event-name=${eventName.replace(/ /g, '+')} group relative inline-flex items-center gap-2 text-[14px] font-semibold text-[#178838] hover:text-[#22A94F] transition-colors ${className}`}
      >
        <span className="relative">
          {children}
          <motion.span
            aria-hidden
            className="absolute -bottom-0.5 left-0 right-0 h-[1.5px] bg-[#22A94F]"
            initial={{ scaleX: 0.2, originX: 0 }}
            animate={{ scaleX: [0.2, 1, 0.2] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          />
        </span>
        <ArrowRight size={iconSize} className="transition-transform group-hover:translate-x-0.5" />
      </Link>
    );
  }

  const v = variants[variant] || variants.green;

  return (
    <span className={`relative inline-flex ${className}`}>
      {/* Breathing halo — very restrained */}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{ boxShadow: `0 0 0 0 ${v.halo}` }}
        animate={{ boxShadow: [
          `0 0 0 0 ${v.halo}`,
          `0 0 0 10px rgba(0,0,0,0)`,
        ] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
      />
      <Link
        to={to}
        data-testid={testId}
        className={`plausible-event-name=${eventName.replace(/ /g, '+')} group relative inline-flex items-center gap-2 rounded-full font-semibold transition-colors ${sizes[size]}`}
        style={{ backgroundColor: v.bg, color: v.text }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = v.bgHover)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = v.bg)}
      >
        <motion.span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(255,255,255,0.28), transparent)' }}
          animate={{ opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="relative">{children}</span>
        <motion.span
          aria-hidden
          className="relative inline-flex"
          animate={{ x: [0, 3, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ArrowRight size={iconSize} />
        </motion.span>
      </Link>
    </span>
  );
}
